import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { Reveal } from "../components/common/Reveal";
import { SectionLabel } from "../components/common/SectionLabel";
import { TechCard } from "../components/common/TechCard";
import { ArrowRight } from "lucide-react";
import { useSection } from "../context/SiteContentContext";
import SEO from "../components/common/SEO";

const DEFAULT_SETTINGS = {
    contact_email: "info@skovomax.com",
    phones: ["+91 7799100060", "+91 7799100059"],
    corporate_address: "302 Model House, Punjagutta, Hyderabad - 500082, Telangana, India",
    factory_address: "Plot #UDL-1, Utterpally Village, Kandi Mandal, Sangareddy - 502295, Telangana, India",
    response_time: "≤ 48h",
    interests: ["LiDAR Systems", "Photonic Sensors", "Fiber Lasers", "GeoAI Analytics", "Platforms", "Research Partnership"],
};

export default function Contact() {
    const settings = useSection("settings", DEFAULT_SETTINGS);
    const INTERESTS = settings.interests && settings.interests.length ? settings.interests : DEFAULT_SETTINGS.interests;
    const [form, setForm] = useState({
        name: "",
        email: "",
        company: "",
        interest: "",
        subject: "",
        message: "",
        website: "", // honeypot — must stay empty for real users
    });
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await api.post("/contact", form);
            toast.success("Inquiry received. We'll respond within 2 business days.");
            setSent(true);
            setForm({ name: "", email: "", company: "", interest: "", subject: "", message: "", website: "" });
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            <SEO
                title="Contact — Open a Channel · Skovomax"
                description="Research collaborations, custom engagements, and demo requests. We read every inquiry — typical response time ≤ 48 hours."
            />
            <section className="pt-28 pb-16 border-b border-white/10">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10">
                    <Reveal>
                        <SectionLabel label="Contact / 09" />
                        <h1 className="font-serif-display text-6xl md:text-8xl leading-[0.95] tracking-tight max-w-5xl">
                            Open a channel.
                        </h1>
                        <p className="mt-8 max-w-2xl text-white/60 text-lg leading-relaxed">
                            Research collaborations, custom engagements, and demo requests. We read every inquiry.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="py-20">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
                    <div className="md:col-span-5 space-y-6">
                        <TechCard className="!p-8">
                            <div className="tech-label mb-2">Primary</div>
                            <div className="font-serif-display text-3xl text-white mb-1">{settings.contact_email}</div>
                            <div className="text-white/50 text-sm">Engineering & partnerships</div>
                        </TechCard>
                        {(settings.phones || []).length > 0 && (
                            <TechCard className="!p-8">
                                <div className="tech-label mb-3">Phone</div>
                                <div className="space-y-2">
                                    {settings.phones.map((p, i) => (
                                        <div key={i} className="font-serif-display text-2xl md:text-3xl text-white">{p}</div>
                                    ))}
                                </div>
                            </TechCard>
                        )}
                        <TechCard className="!p-8">
                            <div className="tech-label mb-3">Corporate Office</div>
                            <div className="text-white/80 text-sm leading-relaxed">
                                {settings.corporate_address}
                            </div>
                        </TechCard>
                        <TechCard className="!p-8">
                            <div className="tech-label mb-3">Factory</div>
                            <div className="text-white/80 text-sm leading-relaxed">
                                {settings.factory_address}
                            </div>
                        </TechCard>
                        <TechCard className="!p-8">
                            <div className="tech-label mb-2">Response Time</div>
                            <div className="font-serif-display text-3xl text-[var(--accent-mercury)]">{settings.response_time}</div>
                            <div className="text-white/50 text-sm mt-1">on business days</div>
                        </TechCard>
                    </div>

                    <div className="md:col-span-7">
                        <TechCard className="!p-10">
                            {sent ? (
                                <div data-testid="contact-success">
                                    <SectionLabel label="Transmission received" />
                                    <h3 className="font-serif-display text-4xl mb-4">Signal acquired.</h3>
                                    <p className="text-white/60 mb-6">
                                        Your inquiry has been logged. A member of the Skovomax team will respond
                                        within 48 business hours.
                                    </p>
                                    <button
                                        onClick={() => setSent(false)}
                                        className="btn-secondary"
                                        data-testid="contact-new-inquiry"
                                    >
                                        Start new inquiry
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
                                    {/* Honeypot — hidden from real users & screen readers. If a bot fills this, we silently drop the submission server-side. */}
                                    <div
                                        aria-hidden="true"
                                        style={{
                                            position: "absolute",
                                            left: "-9999px",
                                            top: "-9999px",
                                            width: "1px",
                                            height: "1px",
                                            overflow: "hidden",
                                            pointerEvents: "none",
                                            opacity: 0,
                                        }}
                                    >
                                        <label htmlFor="hp-website">Website (leave empty)</label>
                                        <input
                                            id="hp-website"
                                            type="text"
                                            name="website"
                                            tabIndex={-1}
                                            autoComplete="off"
                                            value={form.website}
                                            onChange={onChange("website")}
                                        />
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="tech-label mb-2 block">Name *</label>
                                            <input
                                                required
                                                minLength={2}
                                                className="tech-input"
                                                value={form.name}
                                                onChange={onChange("name")}
                                                data-testid="contact-name"
                                            />
                                        </div>
                                        <div>
                                            <label className="tech-label mb-2 block">Email *</label>
                                            <input
                                                required
                                                type="email"
                                                className="tech-input"
                                                value={form.email}
                                                onChange={onChange("email")}
                                                data-testid="contact-email"
                                            />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="tech-label mb-2 block">Organization</label>
                                            <input
                                                className="tech-input"
                                                value={form.company}
                                                onChange={onChange("company")}
                                                data-testid="contact-company"
                                            />
                                        </div>
                                        <div>
                                            <label className="tech-label mb-2 block">Area of Interest</label>
                                            <select
                                                className="tech-input"
                                                value={form.interest}
                                                onChange={onChange("interest")}
                                                data-testid="contact-interest"
                                            >
                                                <option value="">Select —</option>
                                                {INTERESTS.map((o) => (
                                                    <option key={o} value={o}>{o}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="tech-label mb-2 block">Subject *</label>
                                        <input
                                            required
                                            className="tech-input"
                                            value={form.subject}
                                            onChange={onChange("subject")}
                                            data-testid="contact-subject"
                                        />
                                    </div>
                                    <div>
                                        <label className="tech-label mb-2 block">Message *</label>
                                        <textarea
                                            required
                                            minLength={10}
                                            rows={6}
                                            className="tech-input"
                                            value={form.message}
                                            onChange={onChange("message")}
                                            data-testid="contact-message"
                                        />
                                    </div>
                                    <div>
                                        <button
                                            type="submit"
                                            className="btn-primary"
                                            disabled={sending}
                                            data-testid="contact-submit"
                                        >
                                            {sending ? "Transmitting…" : "Send Inquiry"} <ArrowRight size={14} />
                                        </button>
                                    </div>
                                </form>
                            )}
                        </TechCard>
                    </div>
                </div>
            </section>
        </>
    );
}
